import React, { useState, useEffect, useMemo, useCallback } from "react";
import MainLayout from "../components/layout/MainLayout";
import {
  BarChart3, Calendar, TrendingUp, Link2, Code, Eye, Settings, ExternalLink, RefreshCw, Compass
} from "lucide-react";

import {
  SeoMarket,
  ContentItem,
  BacklinkItem,
  KeywordItem,
  CompetitorItem,
  PusherSettings,
  PusherState,
  RadarData,
  SeoOverview
} from "./seo/types";

import {
  INITIAL_CONTENT,
  INITIAL_BACKLINKS,
  INITIAL_KEYWORDS,
  INITIAL_COMPETITORS,
  DEFAULT_SETTINGS
} from "./seo/defaultData";

import SeoOverviewTab from "./seo/SeoOverviewTab";
import SeoRoadmapTab from "./seo/SeoRoadmapTab";
import SeoContentTab, { ContentEngine } from "./seo/SeoContentTab";
import SeoKeywordsTab from "./seo/SeoKeywordsTab";
import SeoBacklinksTab from "./seo/SeoBacklinksTab";
import SeoTechnicalTab from "./seo/SeoTechnicalTab";
import SeoCompetitorsTab from "./seo/SeoCompetitorsTab";
import SeoSettingsTab from "./seo/SeoSettingsTab";
import GenerateArticleDialog from "./seo/GenerateArticleDialog";
import { MODE_LABEL } from "./seo/engineFormat";
import { seoApi, SeoApiError } from "../services/seoApi";

type TabType = "overview" | "roadmap" | "content" | "keywords" | "backlinks" | "technical" | "competitors" | "settings";
type EngineStatus = "checking" | "connected" | "offline";
type Notice = { kind: "success" | "info" | "error"; text: string; url?: string };

const ENGINE_MARKETS: SeoMarket[] = ["SE", "NO", "FI", "DK", "DE", "ZA", "FR", "ES"];

// Backend keyword rows carry numeric ids and nullable metrics; the tabs work with KeywordItem.
function toKeywordItem(k: any): KeywordItem {
  return {
    ...k,
    id: String(k.id),
    difficulty: k.difficulty ?? 0,
    searchVolume: k.searchVolume ?? 0,
    targetUrl: k.targetUrl || "",
    lastChecked: k.lastChecked ? String(k.lastChecked).slice(0, 10) : "–",
    gscImpressions: k.gscImpressions ?? undefined,
    gscClicks: k.gscClicks ?? undefined,
  };
}

export default function SimplyloanSeoPage() {
  // Active Tab state
  const [activeTab, setActiveTab] = useState<TabType>("overview");

  // Active Market / Country state (Default: SE)
  const [activeMarket, setActiveMarket] = useState<SeoMarket>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_market_v2");
      return (saved as SeoMarket) || "SE";
    } catch {
      return "SE";
    }
  });

  // ─── Local planning data (used when the SEO engine backend is not reachable) ───
  const [contentList, setContentList] = useState<ContentItem[]>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_content_v2");
      if (saved) return JSON.parse(saved);
      const v1 = localStorage.getItem("simplyloan_seo_content_v1");
      if (v1) {
        const parsed = JSON.parse(v1);
        return parsed.map((item: any) => ({ ...item, market: item.market || "SE" }));
      }
      return INITIAL_CONTENT;
    } catch {
      return INITIAL_CONTENT;
    }
  });

  const [backlinksList, setBacklinksList] = useState<BacklinkItem[]>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_backlinks_v3");
      if (saved) return JSON.parse(saved);
      return INITIAL_BACKLINKS;
    } catch {
      return INITIAL_BACKLINKS;
    }
  });

  const [keywordsList, setKeywordsList] = useState<KeywordItem[]>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_keywords_v1");
      return saved ? JSON.parse(saved) : INITIAL_KEYWORDS;
    } catch {
      return INITIAL_KEYWORDS;
    }
  });

  const [competitorsList, setCompetitorsList] = useState<CompetitorItem[]>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_competitors_v1");
      return saved ? JSON.parse(saved) : INITIAL_COMPETITORS;
    } catch {
      return INITIAL_COMPETITORS;
    }
  });

  const [settings, setSettings] = useState<PusherSettings>(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_settings_v2");
      if (saved) return JSON.parse(saved);
      const v1 = localStorage.getItem("simplyloan_seo_settings_v1");
      if (v1) return JSON.parse(v1);
      return DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // ─── SEO engine state ───
  const [engineStatus, setEngineStatus] = useState<EngineStatus>("checking");
  const [engineError, setEngineError] = useState<string | null>(null);
  const [overview, setOverview] = useState<SeoOverview | null>(null);
  const [pusherState, setPusherState] = useState<PusherState | null>(null);
  const [radar, setRadar] = useState<RadarData | null>(null);
  const [engineContent, setEngineContent] = useState<ContentItem[]>([]);
  const [engineKeywords, setEngineKeywords] = useState<KeywordItem[]>([]);
  const [cannibalization, setCannibalization] = useState<any[]>([]);
  const [engineSettings, setEngineSettings] = useState<PusherSettings>(DEFAULT_SETTINGS);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const connected = engineStatus === "connected";

  // Sync local planning data to localStorage
  useEffect(() => {
    localStorage.setItem("simplyloan_seo_market_v2", activeMarket);
  }, [activeMarket]);

  useEffect(() => {
    localStorage.setItem("simplyloan_seo_content_v2", JSON.stringify(contentList));
  }, [contentList]);

  useEffect(() => {
    localStorage.setItem("simplyloan_seo_backlinks_v3", JSON.stringify(backlinksList));
  }, [backlinksList]);

  useEffect(() => {
    localStorage.setItem("simplyloan_seo_keywords_v1", JSON.stringify(keywordsList));
  }, [keywordsList]);

  useEffect(() => {
    localStorage.setItem("simplyloan_seo_competitors_v1", JSON.stringify(competitorsList));
  }, [competitorsList]);

  useEffect(() => {
    localStorage.setItem("simplyloan_seo_settings_v2", JSON.stringify(settings));
  }, [settings]);

  const loadEngine = useCallback(async (market: SeoMarket) => {
    if (!ENGINE_MARKETS.includes(market)) {
      setEngineStatus("offline");
      setEngineError(null);
      return;
    }
    try {
      const [ov, content, pusher, keywords, comp, cannibal] = await Promise.all([
        seoApi.overview(market), seoApi.content(market), seoApi.pusher(market), seoApi.keywords(market),
        seoApi.competitors(market), seoApi.cannibalization(market),
      ]);
      setOverview(ov);
      setEngineContent(content.items);
      setPusherState(pusher);
      setEngineSettings({ ...DEFAULT_SETTINGS, ...pusher.settings });
      setEngineKeywords(keywords.items.map(toKeywordItem));
      setRadar(comp);
      setCannibalization(cannibal.items);
      setEngineStatus("connected");
      setEngineError(null);
    } catch (e) {
      setEngineStatus("offline");
      setEngineError(e instanceof SeoApiError ? `${e.status}: ${e.message}` : "Ingen forbindelse til API'et");
    }
  }, []);

  useEffect(() => {
    setEngineStatus("checking");
    loadEngine(activeMarket);
  }, [activeMarket, loadEngine]);

  // Data the tabs see: the engine's when connected, otherwise the local planning lists
  const marketContent = useMemo(() => {
    return connected ? engineContent : contentList.filter(c => c.market === activeMarket);
  }, [connected, engineContent, contentList, activeMarket]);

  const readyBufferCount = useMemo(() => {
    if (connected && pusherState) return pusherState.buffer.readyCount;
    return marketContent.filter(c => c.status === "ready_buffer" || c.status === "scheduled").length;
  }, [connected, pusherState, marketContent]);

  const daysBufferRemaining = connected && pusherState
    ? Math.round(pusherState.buffer.daysCovered)
    : Math.round(readyBufferCount * 3.5);

  const isBufferLow = connected && pusherState ? pusherState.buffer.status !== "healthy" : readyBufferCount < 3;

  // "Push next article now": only the backend can publish; offline nothing pretends to go live.
  const handleTriggerManualPush = async (contentId?: string) => {
    if (!connected) {
      setNotice({ kind: "info", text: "SEO-motoren er ikke forbundet, så intet er udgivet. Udgivelse sker kun via backend." });
      return;
    }
    setIsDeploying(true);
    setNotice(null);
    try {
      const res = await seoApi.triggerPush(activeMarket, contentId);
      if (res.status === "publishing") {
        const deploy = res.deploy?.status === "triggered" ? "Site-build er startet."
          : "Ingen build-hook sat op: artiklen går live med næste site-deploy.";
        setNotice({ kind: "success", text: `Artiklen er lagt i site-eksporten. ${deploy} Den markeres som live, når URL'en svarer.`, url: res.url });
      } else if (res.status === "dry_run") {
        setNotice({ kind: "info", text: `Dry-run: ville have udgivet ${res.url}. Sæt SEO_PUBLISH_MODE=live for at udgive.` });
      }
    } catch (e) {
      const payload = e instanceof SeoApiError ? e.payload : null;
      const text = payload?.status === "blocked"
        ? `Kvalitetstjekket blokerede udgivelsen (${(payload.blocking || []).join(", ")}). Artiklen er sendt tilbage til review.`
        : payload?.message || (e instanceof Error ? e.message : "Udgivelsen fejlede");
      setNotice({ kind: payload?.status === "empty" ? "info" : "error", text });
    } finally {
      setIsDeploying(false);
      loadEngine(activeMarket);
    }
  };

  const contentEngine: ContentEngine | undefined = connected ? {
    switches: overview?.switches ?? null,
    transition: async (id, to, opts) => {
      try {
        return await seoApi.transition(activeMarket, id, to, opts);
      } finally {
        loadEngine(activeMarket);
      }
    },
    create: async (payload) => {
      await seoApi.createContent(activeMarket, { ...payload, status: "idea" });
      await loadEngine(activeMarket);
    },
    update: async (id, patch) => {
      const res = await seoApi.updateContent(activeMarket, id, patch);
      await loadEngine(activeMarket);
      return res;
    },
    qualityCheck: async (id) => {
      const report = await seoApi.qualityCheck(activeMarket, id);
      await loadEngine(activeMarket);
      return report;
    },
    openGenerate: () => setIsGenerateOpen(true),
    publishNow: async (id) => {
      await handleTriggerManualPush(id);
      return "";
    },
    submitUrl: (id) => seoApi.submitUrl(activeMarket, { contentId: id }),
    inspectUrl: (id) => seoApi.inspectUrl(activeMarket, { contentId: id }),
    loadDetail: (id) => seoApi.contentDetail(activeMarket, id),
  } : undefined;

  const countries: { id: SeoMarket; name: string; flag: string; status: "active" | "coming_soon" }[] = [
    { id: "SE", name: "Sverige", flag: "🇸🇪", status: "active" },
    { id: "NO", name: "Norge", flag: "🇳🇴", status: "active" },
    { id: "FI", name: "Finland", flag: "🇫🇮", status: "active" },
    { id: "DK", name: "Danmark", flag: "🇩🇰", status: "active" },
    { id: "DE", name: "Tyskland", flag: "🇩🇪", status: "active" },
    { id: "ZA", name: "Sydafrika", flag: "🇿🇦", status: "active" },
    { id: "FR", name: "Frankrig", flag: "🇫🇷", status: "active" },
    { id: "ES", name: "Spanien", flag: "🇪🇸", status: "active" }
  ];

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: "overview", label: "Overview", icon: <BarChart3 className="w-4 h-4" /> },
    { id: "roadmap", label: "Roadmap & Gantt (Q4–Q1)", icon: <Compass className="w-4 h-4" /> },
    { id: "content", label: "Content Pipeline", icon: <Calendar className="w-4 h-4" /> },
    { id: "keywords", label: "Keywords & SERP", icon: <TrendingUp className="w-4 h-4" /> },
    { id: "backlinks", label: "Backlinks & Outreach", icon: <Link2 className="w-4 h-4" /> },
    { id: "technical", label: "Teknisk SEO & Schema", icon: <Code className="w-4 h-4" /> },
    { id: "competitors", label: "Konkurrenter & Ads", icon: <Eye className="w-4 h-4" /> },
    { id: "settings", label: "Indstillinger & Backup", icon: <Settings className="w-4 h-4" /> },
  ];

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto pb-16">

        {/* ─── Top Header Card with Country Switcher ─────────────────────────── */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-5">

          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                SimplyLoan SEO & Media Hub
              </h1>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                connected ? "bg-[#0F3F34]/10 text-[#0F3F34]"
                  : engineStatus === "checking" ? "bg-gray-100 text-gray-500" : "bg-amber-100 text-amber-800"}`}
                title={engineError || undefined}>
                {connected
                  ? `SEO-motor forbundet · udgivelse ${MODE_LABEL[overview?.switches.publish || "dry_run"]}`
                  : engineStatus === "checking" ? "Forbinder…" : "Lokal planlægning (motor offline)"}
              </span>
              {connected && (
                <button onClick={() => loadEngine(activeMarket)} className="text-gray-400 hover:text-gray-700" title="Opdater">
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Content-buffer og udgivelsesrytme, nyhedsagent, placeringer, konkurrent-radar og backlinks
            </p>
          </div>

          {/* Country / Market Picker */}
          <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-200">
            {countries.map((c) => {
              const isSelected = activeMarket === c.id;
              const isComingSoon = c.status === "coming_soon";

              return (
                <button
                  key={c.id}
                  onClick={() => {
                    if (isComingSoon) {
                      alert(`${c.name} lanceres i fase 2. Vi fokuserer 100% på Sverige og Norge lige nu.`);
                      return;
                    }
                    setActiveMarket(c.id);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                    isSelected
                      ? "bg-[#0F3F34] text-white shadow-xs"
                      : isComingSoon
                      ? "text-gray-400 hover:text-gray-600 opacity-60"
                      : "text-gray-700 hover:bg-gray-200/70"
                  }`}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span>{c.name}</span>
                  {isComingSoon && (
                    <span className="text-[9px] uppercase px-1 rounded bg-gray-200 text-gray-600 font-normal">
                      Snart
                    </span>
                  )}
                </button>
              );
            })}
          </div>

        </div>

        {engineStatus === "offline" && ENGINE_MARKETS.includes(activeMarket) && (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            SEO-motoren svarer ikke{engineError ? ` (${engineError})` : ""}. Du ser de lokale planlægningsdata fra denne
            browser; intet bliver udgivet herfra. Backend: <code>seo_engine</code> i Lambdaen og migrationen
            <code> seo_engine_migrate</code>.
          </div>
        )}

        {notice && (
          <div className={`p-4 rounded-xl border text-sm font-medium flex items-center justify-between gap-3 ${
            notice.kind === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : notice.kind === "error" ? "bg-red-50 border-red-200 text-red-900" : "bg-slate-50 border-slate-200 text-slate-800"}`}>
            <span>{notice.text}</span>
            <span className="flex items-center gap-3 shrink-0">
              {notice.url && (
                <a href={notice.url} target="_blank" rel="noreferrer" className="underline font-bold text-xs flex items-center gap-1">
                  Åbn URL <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button onClick={() => setNotice(null)} className="text-xs opacity-60 hover:opacity-100">Luk</button>
            </span>
          </div>
        )}

        {/* ─── Multi-Tab Navigation Bar ──────────────────────────────────────── */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex gap-6 min-w-max" aria-label="Tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 pb-3 px-1 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === t.id
                    ? "border-[#0F3F34] text-[#0F3F34]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.icon}
                <span>{t.label}</span>
                {t.id === "content" && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    isBufferLow ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                  }`}>
                    {readyBufferCount}
                  </span>
                )}
                {t.id === "competitors" && connected && (overview?.openAlerts ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                    {overview?.openAlerts}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ─── TAB CONTENTS ──────────────────────────────────────────────────── */}

        {activeTab === "overview" && (
          <SeoOverviewTab
            market={activeMarket}
            contentList={marketContent}
            backlinksList={backlinksList.filter(b => b.market === activeMarket)}
            keywordsList={connected ? engineKeywords : keywordsList.filter(k => k.market === activeMarket)}
            competitorsList={competitorsList.filter(c => c.market === activeMarket)}
            settings={connected ? engineSettings : settings}
            onTabChange={(tab) => setActiveTab(tab)}
            onTriggerPush={() => handleTriggerManualPush()}
            isDeploying={isDeploying}
            readyBufferCount={readyBufferCount}
            daysBufferRemaining={daysBufferRemaining}
            isBufferLow={isBufferLow}
            engineConnected={connected}
            pusherState={connected ? pusherState : null}
            overview={connected ? overview : null}
          />
        )}

        {activeTab === "roadmap" && (
          <SeoRoadmapTab
            market={activeMarket}
            overview={connected ? overview : null}
            pusherState={connected ? pusherState : null}
          />
        )}

        {activeTab === "content" && (
          <SeoContentTab
            market={activeMarket}
            contentList={connected ? engineContent : contentList}
            setContentList={setContentList}
            settings={connected ? engineSettings : settings}
            onTriggerPush={() => handleTriggerManualPush()}
            isDeploying={isDeploying}
            engine={contentEngine}
          />
        )}

        {activeTab === "keywords" && (
          <SeoKeywordsTab
            market={activeMarket}
            keywordsList={connected ? engineKeywords : keywordsList}
            setKeywordsList={setKeywordsList}
            contentList={connected ? engineContent : contentList}
            engine={connected ? {
              addKeywords: async (keywords, intent, targetUrl) => {
                await seoApi.addKeywords(activeMarket, keywords, { intent, targetUrl: targetUrl || undefined });
                await loadEngine(activeMarket);
              },
              cannibalization,
            } : undefined}
          />
        )}

        {activeTab === "backlinks" && (
          <SeoBacklinksTab
            market={activeMarket}
            backlinksList={backlinksList}
            setBacklinksList={setBacklinksList}
          />
        )}

        {activeTab === "technical" && (
          <SeoTechnicalTab
            market={activeMarket}
          />
        )}

        {activeTab === "competitors" && (
          <SeoCompetitorsTab
            market={activeMarket}
            competitorsList={competitorsList}
            setCompetitorsList={setCompetitorsList}
            radar={connected ? radar : null}
          />
        )}

        {activeTab === "settings" && (
          <SeoSettingsTab
            market={activeMarket}
            settings={connected ? engineSettings : settings}
            setSettings={connected ? setEngineSettings : setSettings}
            contentList={contentList}
            setContentList={setContentList}
            backlinksList={backlinksList}
            setBacklinksList={setBacklinksList}
            keywordsList={keywordsList}
            setKeywordsList={setKeywordsList}
            competitorsList={competitorsList}
            setCompetitorsList={setCompetitorsList}
            engine={connected ? {
              switches: overview?.switches ?? null,
              save: async (s) => {
                await seoApi.savePusherSettings(activeMarket, {
                  enabled: s.enabled, activeDays: s.activeDays, windowStart: s.windowStart, windowEnd: s.windowEnd,
                  jitterMinMinutes: s.jitterMinMinutes, jitterMaxMinutes: s.jitterMaxMinutes,
                  postsPerActiveDay: s.postsPerActiveDay, targetBufferMinDays: s.targetBufferMinDays,
                  skipHolidays: s.skipHolidays, newsFastLane: s.newsFastLane,
                });
                await loadEngine(activeMarket);
              },
            } : undefined}
          />
        )}

        {isGenerateOpen && (
          <GenerateArticleDialog
            market={activeMarket}
            switches={overview?.switches ?? null}
            onClose={() => setIsGenerateOpen(false)}
            onDone={() => loadEngine(activeMarket)}
          />
        )}

      </div>
    </MainLayout>
  );
}
